import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { reportsAPI } from '../lib/api';
import { 
    Download, 
    Upload, 
    FileText,
    FileSpreadsheet,
    DollarSign,
    Clock,
    FolderKanban,
    Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { useRef } from 'react';
import { toast } from 'sonner';

export default function Reports() {
    const [summaries, setSummaries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [downloadingXlsx, setDownloadingXlsx] = useState(false);
    const fileInputRef = useRef(null);

    // Excel report state
    const today = new Date().toISOString().split('T')[0];
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        .toISOString().split('T')[0];
    const [selectedProject, setSelectedProject] = useState('all');
    const [dateFrom, setDateFrom] = useState(firstOfMonth);
    const [dateTo, setDateTo] = useState(today);

    useEffect(() => {
        fetchSummaries();
    }, []);

    const fetchSummaries = async () => {
        try {
            const response = await reportsAPI.getSummary();
            setSummaries(response.data);
            // Extract unique projects from summaries
            setProjects(response.data.map(s => ({
                id: s.project_id,
                name: s.project_name,
                billing_number: s.billing_number
            })));
        } catch (error) {
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleExportJson = async () => {
        setExporting(true);
        try {
            const response = await reportsAPI.exportData();
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `swisstime-export-${today}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Data exported successfully');
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    const handleDownloadXlsx = async () => {
        if (!dateFrom || !dateTo) {
            toast.error('Please select a date range');
            return;
        }
        if (dateFrom > dateTo) {
            toast.error('Start date must be before end date');
            return;
        }
        setDownloadingXlsx(true);
        try {
            const response = await reportsAPI.downloadXlsx({
                project_id: selectedProject === 'all' ? null : selectedProject,
                date_from: dateFrom,
                date_to: dateTo,
            });
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `client-report-${dateFrom}-to-${dateTo}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Report downloaded');
        } catch (error) {
            toast.error('Failed to generate report');
        } finally {
            setDownloadingXlsx(false);
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            await reportsAPI.importData(data);
            toast.success('Data imported successfully');
            fetchSummaries();
        } catch (error) {
            if (error instanceof SyntaxError) {
                toast.error('Invalid JSON file');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to import data');
            }
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const totalHours = summaries.reduce((sum, s) => sum + s.total_hours, 0);
    const totalCost  = summaries.reduce((sum, s) => sum + s.total_cost, 0);
    const totalTasks = summaries.reduce((sum, s) => sum + s.task_count, 0);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-zinc-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8" data-testid="reports-page">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Reports
                        </h1>
                        <p className="text-lg text-zinc-500">Project summaries and client reports</p>
                    </div>
                    <div className="flex gap-3">
                        <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
                        <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} className="swiss-btn-secondary">
                            <Upload className="w-4 h-4 mr-2" />
                            {importing ? 'Importing...' : 'Import'}
                        </Button>
                        <Button onClick={handleExportJson} disabled={exporting} className="swiss-btn-secondary">
                            <Download className="w-4 h-4 mr-2" />
                            {exporting ? 'Exporting...' : 'Export JSON'}
                        </Button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-fade-in">
                    <div className="swiss-card">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                                <FolderKanban className="w-5 h-5 text-orange-600" />
                            </div>
                            <span className="caption">Projects</span>
                        </div>
                        <p className="kpi-number">{summaries.length}</p>
                    </div>
                    <div className="swiss-card">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-zinc-100 rounded-sm flex items-center justify-center">
                                <FileText className="w-5 h-5 text-zinc-600" />
                            </div>
                            <span className="caption">Tasks</span>
                        </div>
                        <p className="kpi-number">{totalTasks}</p>
                    </div>
                    <div className="swiss-card">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                                <Clock className="w-5 h-5 text-blue-600" />
                            </div>
                            <span className="caption">Total Hours</span>
                        </div>
                        <p className="kpi-number">{totalHours.toFixed(1)}</p>
                    </div>
                    <div className="swiss-card bg-zinc-900">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-sm font-medium tracking-wide uppercase text-zinc-400">Total Value</span>
                        </div>
                        <p className="text-5xl font-extrabold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>

                {/* ── CLIENT REPORT GENERATOR ── */}
                <div className="swiss-card animate-fade-in">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Client Report
                            </h2>
                            <p className="text-sm text-zinc-500">Generate a formatted Excel report by date range</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                        <div className="form-group">
                            <Label className="form-label">Project</Label>
                            <Select value={selectedProject} onValueChange={setSelectedProject}>
                                <SelectTrigger>
                                    <SelectValue placeholder="All projects" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Projects</SelectItem>
                                    {projects.map(p => (
                                        <SelectItem key={p.id} value={p.id}>
                                            {p.name} (#{p.billing_number})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                From
                            </Label>
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.target.value)}
                                className="swiss-input"
                            />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                To
                            </Label>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                className="swiss-input"
                            />
                        </div>
                        <div>
                            <Button
                                onClick={handleDownloadXlsx}
                                disabled={downloadingXlsx}
                                className="swiss-btn-primary w-full"
                            >
                                <FileSpreadsheet className="w-4 h-4 mr-2" />
                                {downloadingXlsx ? 'Generating...' : 'Download .xlsx'}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Project Summary Table */}
                <div className="animate-fade-in">
                    <h2 className="text-2xl font-bold tracking-tight text-zinc-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Project Summary
                    </h2>
                    {summaries.length === 0 ? (
                        <div className="swiss-card">
                            <div className="empty-state">
                                <FileText className="empty-state-icon" />
                                <h3 className="text-xl font-semibold text-zinc-900 mb-2">No data to report</h3>
                                <p className="text-zinc-500">Create projects and log time entries to see reports</p>
                            </div>
                        </div>
                    ) : (
                        <div className="swiss-card overflow-x-auto">
                            <table className="swiss-table">
                                <thead>
                                    <tr>
                                        <th>Project</th>
                                        <th>Billing Number</th>
                                        <th>Tasks</th>
                                        <th>Hours</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaries.map(summary => (
                                        <tr key={summary.project_id} className="data-row">
                                            <td>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-orange-100 rounded-sm flex items-center justify-center">
                                                        <FolderKanban className="w-4 h-4 text-orange-600" />
                                                    </div>
                                                    <span className="font-medium text-zinc-900">{summary.project_name}</span>
                                                </div>
                                            </td>
                                            <td><span className="font-mono text-sm text-zinc-600">#{summary.billing_number}</span></td>
                                            <td className="text-zinc-600">{summary.task_count}</td>
                                            <td className="font-semibold">{summary.total_hours.toFixed(1)}h</td>
                                            <td className="text-emerald-600 font-semibold">${summary.total_cost.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-zinc-200">
                                        <td colSpan={2} className="font-bold text-zinc-900 pt-4">Grand Total</td>
                                        <td className="font-bold text-zinc-900 pt-4">{totalTasks}</td>
                                        <td className="font-bold text-zinc-900 pt-4">{totalHours.toFixed(1)}h</td>
                                        <td className="font-bold text-emerald-600 pt-4">${totalCost.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
